/**
 * ConfirmAndBook — the ONE-PAGE public booking flow (mockup §0b + §4).
 * Replaces the legacy 3-tab CustomerExecuteShipmentTabs for public users:
 * rate summary → contact (email prefilled from the unlock) → company →
 * shipper/receiver → customs broker → sticky total + single [Confirm booking]
 * CTA. The credit application ("Apply for payment terms") and the agent path
 * are optional POST-booking follow-ups (BookingSuccess) — they never block
 * the booking. Submission: invisible reCAPTCHA → rate_gate_actions
 * .submitPublicBooking (pure payload builder + booking action + CRM event).
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Checkbox, Form, Icon, message } from 'antd';
import Recaptcha from 'react-google-invisible-recaptcha';

import * as actions from '../../../actions';
import { BrokerOption } from '../../../util/publicBookingPayload';
import { PRIME_FREIGHT_BROKERAGE_FEE } from '../../../util/getBookingInfo';
import { G_RECAPTCHA_SITE_KEY } from '../../../envVariables';
import { color } from '../../../styles/designTokens';

import RateSummaryCard from './RateSummaryCard';
import ContactSection from './ContactSection';
import CompanySection from './CompanySection';
import ShipperSection from './ShipperSection';
import BrokerSection from './BrokerSection';
import AdditionalServicesSection from './AdditionalServicesSection';
import AddonsErrorBoundary from './AddonsErrorBoundary';
import BookingSuccess from './BookingSuccess';
import {
  Page,
  PageInner,
  HeaderBar,
  PageTitle,
  QuietButton,
  PrimaryCta,
  FooterBar,
  FooterInner,
  TotalBlock,
  TotalLabel,
  TotalAmount,
} from './confirmTheme';

const SUBMIT_ERROR_COPY =
  'We couldn’t submit your booking just now — please try again.';

/** Required for the CTA to arm (broker name joins when "own broker"). */
const REQUIRED_FIELDS = Object.freeze([
  'role',
  'first_name',
  'last_name',
  'email',
  'phone',
  'company_name',
  'street',
  'city',
  'country',
  'shipper_company',
  'shipper_street',
  'shipper_city',
  'shipper_country',
  'broker_option',
]);

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === '';

const formatMoney = (value) =>
  `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

class ConfirmAndBook extends Component {
  state = {
    submitting: false,
    bookingRef: null,
    booked: false,
    // Optional add-ons (AdditionalServicesSection) — same shapes FInalView uses
    // for the logged-in booking confirmation, so the reused CargoInsurance /
    // DutyAndTax children behave identically for public visitors.
    commodityArr: [{}],
    insuranceArr: [],
    dutyTax: [{}],
    dutyTaxSubtotal: { duties: 0, taxes: 0 },
    warehouseRequested: false,
    warehouseNote: '',
  };

  pendingValues = null;

  componentDidMount() {
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0 });
    }
    // Cargo-insurance category dropdown needs the Breeze commodity list; the
    // fetch is idempotent and public-safe (loadCommodityCategoryList).
    if (this.props.loadCommodityCategoryList) {
      this.props.loadCommodityCategoryList();
    }
  }

  // ── optional add-ons: insurance (ported from FInalView) ─────────────────────

  handleInsuranceAmountChange = (insurance) => {
    this.setState({ insuranceArr: insurance });
  };

  handleCommodityChange = (index, key, value) => {
    // Functional setState (derives from previous state) + immutable update:
    // CargoInsurance's componentDidUpdate diffs commodityArr by content, so it
    // needs a new ref, and the update must not race on a stale this.state.
    this.setState((prev) => ({
      commodityArr: prev.commodityArr.map((row, idx) =>
        idx === index ? { ...row, [key]: value } : row
      ),
    }));
  };

  handleAddCommodity = () => {
    this.setState((prev) => ({
      commodityArr: [
        ...prev.commodityArr,
        { category: '', description: '', commodityValue: '' },
      ],
    }));
  };

  handleDeleteCommodity = (index) => {
    this.setState(
      (prev) => ({ commodityArr: prev.commodityArr.filter((_, idx) => idx !== index) }),
      () => this.props.form.setFieldsValue({ commodityArr: this.state.commodityArr })
    );
  };

  // Sum commodity premiums with the same NaN guard + bookedOnly gate FInalView
  // uses (#19/#20): only a created policy contributes to the agreed total, so the
  // displayed total and the booked insuranceFee agree.
  sumPremiums = (insuranceArr, bookedOnly = false) =>
    (insuranceArr || []).reduce((sum, item) => {
      if (
        bookedOnly &&
        !(item?.quote?.result_code === 'quote_created' && item?.policy_id != null)
      ) {
        return sum;
      }
      const premium = Number(item?.quote?.premium_amount);
      return sum + (Number.isNaN(premium) ? 0 : premium);
    }, 0);

  // ── optional add-ons: duties & taxes (ported from FInalView) ────────────────

  handleDutyTaxChange = (index, key, value) => {
    // Functional setState + immutable update (no in-place state mutation).
    this.setState((prev) => ({
      dutyTax: prev.dutyTax.map((row, idx) =>
        idx === index ? { ...row, [key]: value } : row
      ),
    }));
  };

  handleAddDutyTax = () => {
    this.setState((prev) => ({
      dutyTax: [
        ...prev.dutyTax,
        { amount: '', description: '', quantity: '', hs_code: '' },
      ],
    }));
  };

  handleDeleteDutyTax = (index) => {
    this.setState(
      (prev) => ({ dutyTax: prev.dutyTax.filter((_, idx) => idx !== index) }),
      () => this.props.form.setFieldsValue({ dutyTax: this.state.dutyTax })
    );
  };

  handleDutyTaxAmountSubtotalChange = (amountSubtotal) => {
    this.setState({
      dutyTaxSubtotal: {
        duties: amountSubtotal?.Duty || 0,
        taxes: amountSubtotal?.VAT || 0,
      },
    });
  };

  // ── optional add-ons: warehousing (lightweight request) ─────────────────────

  handleWarehouseToggle = (checked) => this.setState({ warehouseRequested: checked });
  handleWarehouseNote = (note) => this.setState({ warehouseNote: note });

  // ── derived state ──────────────────────────────────────────────────────────

  getTotal = () => {
    const { final_search_form } = this.props;
    const rate = Number(
      final_search_form &&
        final_search_form.selected_card_json &&
        final_search_form.selected_card_json.bookNowPrice
    );
    const base = Number.isNaN(rate) ? 0 : rate;
    const option = this.props.form.getFieldValue('broker_option');
    const brokerFee =
      !option || option === BrokerOption.PRIME_FREIGHT
        ? PRIME_FREIGHT_BROKERAGE_FEE
        : 0;
    // Optional add-ons fold into the total exactly as FInalView's
    // calculateTotalPrice does: booked-policy insurance premium + duties + taxes.
    const insurance = this.sumPremiums(this.state.insuranceArr, true);
    const { duties, taxes } = this.state.dutyTaxSubtotal;
    const duty = Number(duties) || 0;
    const tax = Number(taxes) || 0;
    return base + brokerFee + insurance + duty + tax;
  };

  /** Premium shown in the add-ons section (all quotes, matching FInalView's card). */
  getInsurancePremium = () => this.sumPremiums(this.state.insuranceArr, false);

  /**
   * Only commodities with a CREATED policy ride onto the booking — so the booked
   * insuranceFee equals the premium in the displayed total (FInalView #20). A
   * quote that never became a policy is an on-screen estimate only.
   */
  getBookedInsurance = () =>
    (this.state.insuranceArr || []).filter(
      (item) => item?.policy_id != null && item?.quote?.result_code === 'quote_created'
    );

  isCtaDisabled = () => {
    const { form } = this.props;
    const values = form.getFieldsValue();

    const required = [...REQUIRED_FIELDS];
    if (values.broker_option === BrokerOption.OTHER) {
      required.push('broker_name');
    }
    if (required.some((field) => isBlank(values[field]))) return true;
    if (!values.agreement) return true;

    // Only the REQUIRED fields can block the CTA. The optional add-ons
    // (insurance / duty-tax) register their own antd fields with validation
    // rules; a half-filled optional row must never disable Confirm booking.
    const fieldsError = form.getFieldsError(required);
    return required.some((field) => fieldsError[field]);
  };

  // ── submit flow ────────────────────────────────────────────────────────────

  onConfirm = () => {
    if (this.state.submitting) return;
    const { form } = this.props;
    // Validate ONLY the required booking fields — the optional add-on rows
    // (insurance / duty-tax) register their own validated fields and must not
    // block Confirm. Payload still reads every field via getFieldsValue().
    const toValidate = [...REQUIRED_FIELDS, 'agreement'];
    if (form.getFieldValue('broker_option') === BrokerOption.OTHER) {
      toValidate.push('broker_name');
    }
    form.validateFields(toValidate, (err) => {
      if (err) return; // inline messages are already showing
      this.pendingValues = form.getFieldsValue();
      this.executeCaptcha();
    });
  };

  executeCaptcha = async () => {
    if (this.recaptcha && this.recaptcha.execute) {
      try {
        await this.recaptcha.execute();
        return; // onCaptchaResolved continues
      } catch (error) {
        console.log('g reCaptcha error:', error);
      }
    }
    // Captcha unavailable — submit and let the server-side verification decide.
    this.submitBooking('');
  };

  onCaptchaResolved = () => {
    const token =
      (this.recaptcha &&
        this.recaptcha.getResponse &&
        this.recaptcha.getResponse()) ||
      '';
    this.submitBooking(token);
    if (this.recaptcha && this.recaptcha.reset) {
      this.recaptcha.reset();
    }
  };

  submitBooking = async (captchaToken) => {
    const values = this.pendingValues;
    this.pendingValues = null;
    if (!values) return;

    this.setState({ submitting: true });

    const result = await this.props.submitPublicBooking({
      captchaToken,
      contact: {
        role: values.role,
        firstName: values.first_name,
        lastName: values.last_name,
        email: values.email,
        phone: values.phone,
      },
      company: {
        companyName: values.company_name,
        street: values.street,
        address2: values.address2,
        city: values.city,
        state: values.state_province,
        postal: values.postal,
        country: values.country,
      },
      shipper: {
        companyName: values.shipper_company,
        firstName: values.shipper_first_name,
        lastName: values.shipper_last_name,
        email: values.shipper_email,
        street: values.shipper_street,
        address2: values.shipper_address2,
        city: values.shipper_city,
        state: values.shipper_state,
        postal: values.shipper_postal,
        country: values.shipper_country,
      },
      broker: {
        option: values.broker_option,
        name: values.broker_name,
      },
      // Optional add-ons — insurance commodities + duty/tax subtotal fold into
      // the payload (buildPublicBookingPayload already supports both); warehouse
      // is a lightweight request (no public RFQ backend) surfaced to sales.
      insurance: this.getBookedInsurance(),
      dutyTax: this.state.dutyTaxSubtotal,
      warehouse: this.state.warehouseRequested
        ? { requested: true, note: this.state.warehouseNote || '' }
        : undefined,
    });

    if (result && result.ok) {
      this.setState({
        submitting: false,
        booked: true,
        bookingRef: result.primeFreightRef || result.bookingId || null,
      });
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0 });
      }
    } else {
      console.error('public booking failed:', result && result.error);
      this.setState({ submitting: false });
      message.error(SUBMIT_ERROR_COPY);
    }
  };

  handleBackToSearch = () => {
    this.props.public_user_submit_button(false);
  };

  // ── render ─────────────────────────────────────────────────────────────────

  render() {
    const {
      form,
      final_search_form,
      queryParams,
      discount,
      userEmailBlurCards,
      commodityList,
    } = this.props;
    const { submitting, booked, bookingRef } = this.state;

    const backButton = (
      <QuietButton onClick={this.handleBackToSearch}>
        <Icon type="arrow-left" /> Back to search
      </QuietButton>
    );

    if (booked) {
      return (
        <Page>
          <PageInner>
            <HeaderBar>
              <PageTitle>Booking received</PageTitle>
              {backButton}
            </HeaderBar>
            <BookingSuccess bookingRef={bookingRef} />
          </PageInner>
        </Page>
      );
    }

    const role = form.getFieldValue('role') || 'Importer';
    const total = this.getTotal();
    const ctaDisabled = this.isCtaDisabled();

    return (
      <Page>
        <PageInner>
          <HeaderBar>
            <PageTitle>Confirm &amp; book</PageTitle>
            {backButton}
          </HeaderBar>

          <Form
            className="public-confirm-book-form"
            autoComplete="off"
            hideRequiredMark
          >
            <RateSummaryCard
              finalSearchForm={final_search_form}
              queryParams={queryParams}
              discount={discount}
            />
            <ContactSection form={form} prefilledEmail={userEmailBlurCards} />
            <CompanySection form={form} />
            <ShipperSection form={form} role={role} />
            <BrokerSection form={form} />

            <AddonsErrorBoundary>
            <AdditionalServicesSection
              form={form}
              queryParams={queryParams}
              finalSearchForm={final_search_form}
              commodityListLoaded={
                Array.isArray(commodityList) && commodityList.length > 0
              }
              commodityArr={this.state.commodityArr}
              insuranceArr={this.state.insuranceArr}
              insurancePremium={this.getInsurancePremium()}
              dutyTax={this.state.dutyTax}
              dutyTaxSubtotal={this.state.dutyTaxSubtotal}
              warehouseRequested={this.state.warehouseRequested}
              warehouseNote={this.state.warehouseNote}
              onInsuranceAmountChange={this.handleInsuranceAmountChange}
              onCommodityChange={this.handleCommodityChange}
              onAddCommodity={this.handleAddCommodity}
              onDeleteCommodity={this.handleDeleteCommodity}
              onDutyTaxChange={this.handleDutyTaxChange}
              onAddDutyTax={this.handleAddDutyTax}
              onDeleteDutyTax={this.handleDeleteDutyTax}
              onDutyTaxAmountSubtotalChange={this.handleDutyTaxAmountSubtotalChange}
              onWarehouseToggle={this.handleWarehouseToggle}
              onWarehouseNote={this.handleWarehouseNote}
            />
            </AddonsErrorBoundary>

            <div style={{ marginTop: 16, padding: '0 2px' }}>
              <Form.Item colon={false} style={{ marginBottom: 0 }}>
                {form.getFieldDecorator('agreement', {
                  valuePropName: 'checked',
                  initialValue: false,
                })(
                  <Checkbox>
                    <span style={{ fontSize: 13, color: color.ink }}>
                      I agree to the{' '}
                      <a
                        href="https://www.primefreight.com/booking-terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://www.primefreight.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </Checkbox>
                )}
              </Form.Item>
            </div>
          </Form>

          {G_RECAPTCHA_SITE_KEY && (
            <Recaptcha
              ref={(ref) => (this.recaptcha = ref)}
              sitekey={G_RECAPTCHA_SITE_KEY}
              onResolved={this.onCaptchaResolved}
              style={{ visibility: 'hidden', width: '0px', height: '0px' }}
            />
          )}
        </PageInner>

        <FooterBar>
          <FooterInner>
            <TotalBlock>
              <TotalLabel>Total</TotalLabel>
              <TotalAmount>{formatMoney(total)} USD</TotalAmount>
            </TotalBlock>
            <PrimaryCta
              loading={submitting}
              disabled={ctaDisabled}
              onClick={this.onConfirm}
            >
              Confirm booking
            </PrimaryCta>
          </FooterInner>
        </FooterBar>
      </Page>
    );
  }
}

ConfirmAndBook.propTypes = {
  final_search_form: PropTypes.object,
  queryParams: PropTypes.object,
  discount: PropTypes.object,
  userEmailBlurCards: PropTypes.string,
};

const WrappedConfirmAndBook = Form.create({ name: 'public_confirm_book' })(
  ConfirmAndBook
);

const mapStateToProps = (state) => ({
  final_search_form: state.route.final_search_form,
  queryParams: state.route.queryParams,
  discount: state.route.discount,
  userEmailBlurCards: state.cache.userEmailBlurCards,
  transactionType: state.code_config.transactionType,
  commodityList: state.route.commodityList,
});

export default withRouter(
  connect(mapStateToProps, actions)(WrappedConfirmAndBook)
);
